import type { UTCTimestamp } from 'lightweight-charts';

import type { GetAccountCalendarDataResponse } from '../models/types/get-account-calendar-data-response';
import type {
    CalendarData,
    TransformedCalendarData,
} from '../models/types/transformed-calendar-data';

export function calendarDataTransformer(
    data: Undefinable<GetAccountCalendarDataResponse>,
): TransformedCalendarData {
    const map = new Map<DateString, CalendarData>();

    if (!data) {
        return map;
    }

    data.days.forEach(
        ({
            deals,
            date,
            average_loss_trade: averageLossTrade,
            average_win_trade: averageWinTrade,
            profit_factor: profitFactor,
            total_profit: totalProfit,
            total_win_percent: totalWinPercent,
            has_note: hasNote,
        }) => {
            const trades: CalendarData['trades'] = [];
            const chartData = new Map<
                UTCTimestamp,
                CalendarData['chartData'][number]
            >();

            (deals ?? [])
                .toSorted((a, b) => a.open_time - b.open_time)
                .forEach(
                    ({
                        type,
                        close_time: closeTime,
                        open_time: time,
                        net_profit: netProfit,
                        net_roi: netROI,
                        ...otherDealsData
                    }) => {
                        const roundedNetProfit = +(netProfit ?? 0).toFixed(2);
                        const roundedNetROI = +(netROI ?? 0).toFixed(2);

                        trades.push({
                            ...otherDealsData,
                            net_profit: roundedNetProfit,
                            close_time: closeTime,
                            open_time: time,
                            net_roi: roundedNetROI,
                            type: type === 0 ? 'buy' : 'sell',
                        });

                        if (chartData.has(closeTime as UTCTimestamp)) {
                            chartData.set(closeTime as UTCTimestamp, {
                                time: closeTime as UTCTimestamp,
                                value:
                                    chartData.get(closeTime as UTCTimestamp)!
                                        .value + roundedNetProfit,
                            });
                        } else {
                            chartData.set(closeTime as UTCTimestamp, {
                                time: closeTime as UTCTimestamp,
                                value: roundedNetProfit,
                            });
                        }
                    },
                );

            if (trades.length) {
                const sortedChartData = Array.from(chartData.values()).sort(
                    ({ time: timeA }, { time: timeB }) =>
                        (timeA as number) - (timeB as number),
                );

                map.set(date, {
                    date,
                    chartData: sortedChartData,
                    trades,
                    average_loss_trade: +(averageLossTrade ?? 0).toFixed(2),
                    average_win_trade: +(averageWinTrade ?? 0).toFixed(2),
                    profit_factor: +(profitFactor ?? 0).toFixed(2),
                    total_win_percent: +(totalWinPercent ?? 0).toFixed(2),
                    total_profit: +(totalProfit ?? 0).toFixed(2),
                    has_note: hasNote,
                });
            }
        },
    );

    return map;
}
